/* Copyright (c) 2026 Xperi. All Rights Reserved.  */

import { TAudioTrack, TTextTrack } from "../types";

export class TrackUtils {
  TAG: string = "TrackUtils";

  private displayText: string = "";

  private codecMapping = {
    AUDIO_FORMAT_AAC: "Stereo",
    AUDIO_FORMAT_DOLBY_DIGITAL: "Dolby Audio™",
    AUDIO_FORMAT_DOLBY_DIGITAL_PLUS: "Dolby Audio Plus™",
    AUDIO_FORMAT_MPEG1: "Stereo",
    AUDIO_FORMAT_MPEG2: "Stereo",
    AUDIO_LANGUAGE_UNKNOWN: "Unknown",
    AUDIO_DESCRIPTION: "Audio Description",
    AUDIO_DESCRIPTION_CONST: "ad"
  };

  private languageMapping = new Map<string, string>([
    ["en", "English"],
    ["eng", "English"],
    ["es", "Spanish"],
    ["spa", "Spanish"],
    ["fr", "French"],
    ["fra", "French"],
    ["fre", "French"],
    ["de", "German"],
    ["deu", "German"],
    ["ger", "German"],
    ["it", "Italian"],
    ["ita", "Italian"],
    ["ja", "Japanese"],
    ["jpn", "Japanese"],
    ["ko", "Korean"],
    ["kor", "Korean"],
    ["zh", "Chinese"],
    ["zho", "Chinese"],
    ["chi", "Chinese"],
    ["mi", "Maori"],
    ["mri", "Maori"],
    ["mao", "Maori"],
    ["hi", "Hindi"],
    ["hin", "Hindi"],
    ["cy", "Welsh"],
    ["cym", "Welsh"],
    ["wel", "Welsh"],
    ["eu", "Basque"],
    ["eus", "Basque"],
    ["baq", "Basque"],
    ["ca", "Catalan"],
    ["cat", "Catalan"],
    ["gl", "Galician"],
    ["glg", "Galician"],
    ["pt", "Portuguese"],
    ["por", "Portuguese"],
    ["sv", "Swedish"],
    ["swe", "Swedish"],
    ["ar", "Arabic"],
    ["ara", "Arabic"],
    ["ru", "Russian"],
    ["rus", "Russian"],
    ["nn", "Norwegian"],
    ["nor", "Norwegian"],
    ["nob", "Norwegian"],
    ["fi", "Finnish"],
    ["fin", "Finnish"],
    ["da", "Danish"],
    ["dan", "Danish"],
    ["gd", "Gaelic"],
    ["gla", "Gaelic"],
    ["tr", "Turkish"],
    ["tur", "Turkish"],
    ["pl", "Polish"],
    ["pol", "Polish"],
    ["fa", "Persian"],
    ["fas", "Persian"],
    ["ur", "Urdu"],
    ["urd", "Urdu"],
    ["et", "Estonian"],
    ["est", "Estonian"],
    ["--", "Multiple"],
    ["mul", "Multiple"],
    ["und", "Unknown"],
    ["", "Unknown"]
  ]);

  /**
   * to map Language code to Language name.
   * @param code Language code
   * @param defaultValue Default display string for
   *                     un-mapped language codes
   * @returns Name of the Language for a given Key
   */
  getLanguageByCode(code: string, defaultValue: string): string {
    const languageName = this.languageMapping.get(code);

    return languageName !== undefined ? languageName : defaultValue;
  }

  /**
   * to map Sub Language code to Language name.
   * @param originalTextId Language code
   * @param mimeType mimeType
   * @returns Name of the Language for a given Key
   */
  getSubLanguageByCode(originalTextId: string, mimeType: string): string {
    const regex = /\d+/g;
    const numbers = originalTextId.match(regex);
    if (mimeType.includes("708"))
      return `DTVCC${numbers ? numbers[0] || "" : ""}`;
    return `CC${numbers ? numbers[0] || "" : ""}`;
  }

  /**
   * to map codec value to string.
   * @param codecVal codec value defined in manifest track
   * @returns text to Display codec
   */
  getAudioCodec(codecVal: string): string {
    if (!codecVal || !codecVal.length) {
      return this.codecMapping.AUDIO_LANGUAGE_UNKNOWN;
    }
    /*
     * Below values are based on best known manifest
     * metadata and may need changes if new possible
     * values are encountered in future
     */
    const codec = codecVal.toLowerCase();

    if (codec.includes("ac-3") || codec.includes("ac3")) {
      this.displayText = this.codecMapping.AUDIO_FORMAT_DOLBY_DIGITAL;
    } else if (
      codec.includes("mp4a") ||
      codec.includes("audio/mpeg") ||
      codec.includes("aac")
    ) {
      this.displayText = this.codecMapping.AUDIO_FORMAT_AAC;
    } else if (
      codec === "mpeg1" ||
      codec === "mpeg-1" ||
      codec === "audio/mpeg-l1"
    ) {
      this.displayText = this.codecMapping.AUDIO_FORMAT_MPEG1;
    } else if (
      codec === "mpeg2" ||
      codec === "mpeg-2" ||
      codec === "audio/mpeg-l2"
    ) {
      this.displayText = this.codecMapping.AUDIO_FORMAT_MPEG2;
    } else if (
      codec === "eac3" ||
      codec === "eac-3" ||
      codec === "ec-3" ||
      codec === "audio/eac3"
    ) {
      this.displayText = this.codecMapping.AUDIO_FORMAT_DOLBY_DIGITAL_PLUS;
    }

    return this.displayText;
  }

  /**
   * to get the display Text of individual track.
   * @param track Audio track
   * @param nameMap map of audioId and Language string
   *
   * @returns display Text of audio track
   */
  private getDisplayTextForAudio(
    track: TAudioTrack,
    nameMap: Map<number, string>
  ): string {
    const mappedTrackName = nameMap.get(track.id);
    let displayText =
      mappedTrackName !== undefined
        ? mappedTrackName
        : this.codecMapping.AUDIO_LANGUAGE_UNKNOWN;

    if (
      track.kind === this.codecMapping.AUDIO_DESCRIPTION_CONST ||
      track.roles.includes("description") ||
      track.roles.includes("describes-video") ||
      /audio description|descriptive audio|\bAD\b/i.test(track.label || "")
    ) {
      return displayText + " - " + this.codecMapping.AUDIO_DESCRIPTION;
    }

    // Add if codec details available
    const codec = this.getAudioCodec(track.codec || "");
    if (codec.length > 0) {
      displayText += " - " + codec;
    }
    return displayText;
  }

  /**
   * to map audioId with Language Map.
   * @param tracks array of audio tracks
   * @returns array of audio track processed data
   */
  getAudioNameMap(tracks: TAudioTrack[]): Map<number, string> {
    const langMap: Map<string, TAudioTrack[]> = new Map();

    tracks.forEach((element) => {
      const group = langMap.get(element.language);
      if (group === undefined) {
        langMap.set(element.language, [element]);
      } else {
        // group takes the reference of langMap therefore updating group will upadate the langMap
        group.push(element);
      }
    });

    const nameMap: Map<number, string> = new Map();
    langMap.forEach((value, key) => {
      const trackArr = value;
      let langName = this.getLanguageByCode(
        key,
        this.codecMapping.AUDIO_LANGUAGE_UNKNOWN
      );

      if (trackArr.length === 1) {
        nameMap.set(trackArr[0].id, langName);
      } else {
        langName += " ";
        for (let i = 0; i < trackArr.length; i += 1) {
          nameMap.set(trackArr[i].id, langName + (i + 1).toString());
        }
      }
    });
    return nameMap;
  }

  /**
   * to get the map of audioId and display text of track.
   * @param tracks array of audio tracks
   * @returns array of audio track processed data
   */
  getAudioTrackMap(tracks: TAudioTrack[]): Map<number, string> {
    const audioTrackMap: Map<number, string> = new Map<number, string>();

    const nameMap = this.getAudioNameMap(tracks);

    tracks.forEach((audioTrack) => {
      audioTrackMap.set(
        audioTrack.id,
        this.getDisplayTextForAudio(audioTrack, nameMap)
      );
    });
    return audioTrackMap;
  }

  /**
   * to get the map of text track id and display language of track.
   * The track label is intentionally ignored; the language tag is mapped to its
   * display name instead, falling back to the raw code when unmapped and to
   * "Unknown" when the track has no language tag.
   * @param tracks array of text tracks
   * @returns map of text track id and mapped language display text
   */
  getTextTrackMap(tracks: TTextTrack[]): Map<number, string> {
    const textTrackMap: Map<number, string> = new Map<number, string>();

    tracks.forEach((textTrack) => {
      // A null, empty or blank language tag normalises to "", which
      // languageMapping resolves to "Unknown" (as it does "und").
      const code = textTrack.language ? textTrack.language.trim() : "";
      const language = this.getLanguageByCode(code, code);
      // Closed captions are shown as "<language> CC"; subtitles use the language as-is.
      const label = this.isClosedCaptionKind(textTrack.kind)
        ? `${language} CC`
        : language;
      textTrackMap.set(textTrack.id, label);
    });
    return textTrackMap;
  }

  /**
   * Whether a text track's kind marks it as a closed caption (as opposed to a
   * subtitle).
   */
  private isClosedCaptionKind(kind: string): boolean {
    const normalized = kind.toLowerCase();
    return normalized === "caption" || normalized === "captions";
  }
}
